import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import {
  Star,
  ArrowLeft,
  Eye,
  CheckCircle,
  AlertCircle,
  Target,
  Calendar,
  Edit,
  FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const MinhaAutoavaliacao = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState({
    autoavaliacoes_pendentes: [],
    autoavaliacoes_finalizadas: [],
    avaliacoes_gestor: [],
    total_pendentes: 0,
    total_finalizadas: 0
  });

  const backendUrl = API;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Buscar minhas autoavaliações pendentes (employee_id = user.id, evaluator_type = 'self', status = 'pending')
      const pendentesResponse = await axios.get(`${backendUrl}/api/performance/evaluations/my-pending`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Buscar minhas autoavaliações finalizadas
      const finalizadasResponse = await axios.get(`${backendUrl}/api/performance/evaluations/my-completed`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Buscar avaliações do meu gestor (para visualização)
      let avaliacoesGestor = [];
      if (user?.id) {
        try {
          const gestorResponse = await axios.get(`${backendUrl}/api/performance/evaluations/for-view/${user.id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
          });
          avaliacoesGestor = gestorResponse.data || [];
        } catch (error) {
          console.log('Sem avaliações do gestor disponíveis:', error);
        }
      }
      
      // Filtrar apenas autoavaliações
      const pendentes = (pendentesResponse.data || []).filter(
        av => av.evaluator_type === 'self' || av.evaluation_type === 'autoavaliacao'
      );
      const finalizadas = (finalizadasResponse.data || []).filter(
        av => av.evaluator_type === 'self' || av.evaluation_type === 'autoavaliacao'
      );
      
      setDados({
        autoavaliacoes_pendentes: pendentes,
        autoavaliacoes_finalizadas: finalizadas,
        avaliacoes_gestor: avaliacoesGestor,
        total_pendentes: pendentes.length,
        total_finalizadas: finalizadas.length
      });
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar suas autoavaliações');
    } finally {
      setLoading(false);
    }
  };

  const iniciarAutoavaliacao = (item) => {
    if (!item.id) {
      toast.error('ID da avaliação não encontrado');
      return;
    }
    
    toast.success('Abrindo formulário de autoavaliação...');
    navigate(`/preencher-avaliacao/${item.id}`);
  };

  const formatarData = (dataString) => {
    if (!dataString) return 'N/A';
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2">Carregando suas autoavaliações...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              onClick={() => window.history.back()}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Minha Autoavaliação</h1>
              <p className="text-gray-600">Olá {user?.name || 'Colaborador'}, aqui estão suas autoavaliações</p>
            </div>
          </div>
        </div>

        {/* Cards de Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
              <AlertCircle className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-orange-600">{dados.total_pendentes}</div>
              <p className="text-xs text-gray-600">Aguardando sua autoavaliação</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Finalizadas</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{dados.total_finalizadas}</div>
              <p className="text-xs text-gray-600">Autoavaliações respondidas</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Do Gestor</CardTitle>
              <Star className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{dados.avaliacoes_gestor.length}</div>
              <p className="text-xs text-gray-600">Avaliações do gestor</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs de Autoavaliações */}
        <Tabs defaultValue="pendentes" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="pendentes">
              Pendentes ({dados.total_pendentes})
            </TabsTrigger>
            <TabsTrigger value="finalizadas">
              Finalizadas ({dados.total_finalizadas})
            </TabsTrigger>
            <TabsTrigger value="avaliacoes-gestor">
              Avaliações do Gestor ({dados.avaliacoes_gestor.length})
            </TabsTrigger>
          </TabsList>

          {/* Aba Pendentes */}
          <TabsContent value="pendentes">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-600" />
                  Autoavaliações Pendentes
                </CardTitle>
                <CardDescription>
                  Autoavaliações aguardando seu preenchimento
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dados.autoavaliacoes_pendentes.length === 0 ? (
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      Parabéns! Você não tem autoavaliações pendentes no momento.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    {dados.autoavaliacoes_pendentes.map((item, index) => (
                      <Card key={index} className="border-l-4 border-l-orange-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">
                                Autoavaliação - {item.cycle?.nome || 'Ciclo de Avaliação'}
                              </h4>
                              <p className="text-gray-600 text-sm mb-2">
                                Período: {item.cycle?.data_inicio ? formatarData(item.cycle.data_inicio) : 'N/A'} - {item.cycle?.data_fim ? formatarData(item.cycle.data_fim) : 'N/A'}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  Criada em: {formatarData(item.created_at)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Target className="h-4 w-4" />
                                  Tipo: Autoavaliação
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className="text-orange-600 border-orange-600">
                                Pendente
                              </Badge>
                              <Button
                                onClick={() => iniciarAutoavaliacao(item)}
                                className="bg-orange-600 hover:bg-orange-700"
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Preencher
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba Finalizadas */}
          <TabsContent value="finalizadas">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  Autoavaliações Finalizadas
                </CardTitle>
                <CardDescription>
                  Suas autoavaliações já respondidas
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dados.autoavaliacoes_finalizadas.length === 0 ? (
                  <Alert>
                    <FileText className="h-4 w-4" />
                    <AlertDescription>
                      Você ainda não finalizou nenhuma autoavaliação.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    {dados.autoavaliacoes_finalizadas.map((item, index) => (
                      <Card key={item.id || index} className="border-l-4 border-l-green-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">
                                Autoavaliação - {item.cycle?.nome || 'Ciclo de Avaliação'}
                              </h4>
                              <p className="text-gray-600 text-sm mb-2">
                                Período: {item.cycle?.data_inicio ? formatarData(item.cycle.data_inicio) : 'N/A'} - {item.cycle?.data_fim ? formatarData(item.cycle.data_fim) : 'N/A'}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  Finalizada em: {formatarData(item.completed_at)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="text-green-600 border-green-600">
                                Finalizada
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/preencher-avaliacao/${item.id}`)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Ver Detalhes
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aba Avaliações do Gestor */}
          <TabsContent value="avaliacoes-gestor">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="h-5 w-5 text-blue-600" />
                  Avaliações do Meu Gestor
                </CardTitle>
                <CardDescription>
                  Avaliações feitas pelo seu gestor (visualização apenas)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {dados.avaliacoes_gestor.length === 0 ? (
                  <Alert>
                    <FileText className="h-4 w-4" />
                    <AlertDescription>
                      Nenhuma avaliação do gestor disponível no momento.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <div className="space-y-4">
                    {dados.avaliacoes_gestor.map((item, index) => (
                      <Card key={item.id || index} className="border-l-4 border-l-blue-500">
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <h4 className="font-semibold text-lg">
                                Avaliação do Gestor - {item.cycle?.nome || 'Ciclo de Avaliação'}
                              </h4>
                              <p className="text-gray-600 text-sm mb-2">
                                Avaliador: {item.evaluator?.nome || 'Gestor'}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-4 w-4" />
                                  Finalizada em: {formatarData(item.completed_at)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Target className="h-4 w-4" />
                                  Tipo: Avaliação do Gestor
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <Badge variant="outline" className="text-blue-600 border-blue-600">
                                Finalizada
                              </Badge>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => navigate(`/preencher-avaliacao/${item.id}`)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Ver Avaliação
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MinhaAutoavaliacao;
